import { Link, useParams } from "react-router-dom";
import { Seo } from "../components/Seo";
import { MessageCircle, MapPin, ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PA_BASE, waLink } from "../config";
import { KineticHero } from "../components/KineticHero";
import { Reveal, Overline } from "../components/motion";
import { ROUTES } from "../data/routes";

export default function RoutePage() {
  const { slug } = useParams();
  const r = ROUTES.find((x) => x.slug === slug);

  if (!r) {
    return (
      <section className="bg-[#F9F9F6] px-5 py-40 sm:px-8" data-testid="route-not-found">
        <div className="mx-auto max-w-[800px]">
          <h1 className="font-display text-4xl tracking-tight">Route not found.</h1>
          <Link to={`${PA_BASE}/destinations`} className="mt-6 inline-block text-sm underline underline-offset-4">
            View all destinations
          </Link>
        </div>
      </section>
    );
  }

  const quoteLink = `${PA_BASE}/charter?departure=${encodeURIComponent(r.from)}&destination=${encodeURIComponent(r.to)}#request`;

  return (
    <>
      <Seo
        title={`${r.from} to ${r.to} Private Jet Charter | JS Private Aviation`}
        description={`Charter a private jet from ${r.from} to ${r.to} — aircraft categories, airports, and FAQs. Sourced individually through independent operators.`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: r.faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }}
      />
      <KineticHero
        testId={`route-hero-${r.slug}`}
        fullHeight={false}
        overline="Route guide"
        lines={[r.from, `→ ${r.to}.`]}
        sub={r.overview}
        image={r.image}
      >
        <Link
          to={quoteLink}
          data-testid="route-quote-cta"
          className="group inline-flex items-center gap-3 bg-white px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-black transition-colors duration-300 hover:bg-white/85"
        >
          Request a Quote
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
        <a
          href={waLink(`Hello — I would like a quote for ${r.from} to ${r.to}.`)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="route-whatsapp-cta"
          className="inline-flex items-center gap-2 border border-white/35 px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
        >
          <MessageCircle size={15} /> WhatsApp a Specialist
        </a>
      </KineticHero>

      <section className="bg-[#F9F9F6] py-24 sm:py-32">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
            <Reveal>
              <Overline>Typical aircraft</Overline>
              <h2 className="mt-3 font-display text-3xl tracking-tight">What usually flies this route.</h2>
              <div className="mt-8 flex flex-wrap gap-3" data-testid="route-aircraft-categories">
                {r.categories.map((c) => (
                  <Link
                    key={c}
                    to={`${PA_BASE}/aircraft`}
                    className="border border-black/15 bg-white px-5 py-2.5 text-[12px] uppercase tracking-[0.16em] text-black/70 transition-colors duration-300 hover:bg-black hover:text-white"
                  >
                    {c}
                  </Link>
                ))}
              </div>
              <p className="mt-6 max-w-md text-[13px] leading-relaxed text-black/50">
                Indicative categories for this sector. The specific aircraft offered depends on your party,
                baggage and availability at the time of enquiry — every trip is sourced individually.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <Overline>Airports</Overline>
              <h2 className="mt-3 font-display text-3xl tracking-tight">Departure & arrival.</h2>
              <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2">
                <div data-testid="route-from-airports">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-black/40">From — {r.from}</p>
                  <ul className="mt-4 space-y-3">
                    {r.fromAirports.map((a) => (
                      <li key={a} className="flex gap-3 text-sm text-black/70">
                        <MapPin size={14} className="mt-0.5 shrink-0 text-black/35" />
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
                <div data-testid="route-to-airports">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-black/40">To — {r.to}</p>
                  <ul className="mt-4 space-y-3">
                    {r.toAirports.map((a) => (
                      <li key={a} className="flex gap-3 text-sm text-black/70">
                        <MapPin size={14} className="mt-0.5 shrink-0 text-black/35" />
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal className="mt-20">
            <Overline>Why charter this route</Overline>
            <div className="mt-8 grid grid-cols-1 gap-px bg-black/10 sm:grid-cols-3">
              {r.why.map((w, i) => (
                <div key={w} className="bg-[#F9F9F6] p-8" data-testid={`route-why-${i}`}>
                  <span className="font-mono text-sm text-black/30">0{i + 1}</span>
                  <p className="mt-4 text-[15px] leading-relaxed text-black/75">{w}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#F9F9F6] pb-24 sm:pb-32">
        <div className="mx-auto max-w-[900px] px-5 sm:px-8">
          <Reveal>
            <Overline>Questions</Overline>
            <h2 className="mt-3 font-display text-3xl tracking-tight">
              {r.from} to {r.to}, answered.
            </h2>
            <Accordion type="single" collapsible className="mt-10" data-testid="route-faq">
              {r.faqs.map((f, i) => (
                <AccordionItem key={i} value={`f${i}`} className="border-black/10">
                  <AccordionTrigger data-testid={`route-faq-q-${i}`} className="text-left text-[15px] font-medium hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-black/60">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#0A0A0A] py-24 text-white">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-8 px-5 sm:px-8">
          <Reveal>
            <h2 className="max-w-xl font-display text-3xl sm:text-4xl tracking-tight leading-tight">
              Flying {r.from} to {r.to}? We'll source the aircraft.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-wrap gap-4">
              <Link
                to={quoteLink}
                data-testid="route-bottom-quote-cta"
                className="inline-flex items-center gap-3 bg-white px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-black transition-colors duration-300 hover:bg-white/85"
              >
                Request a Quote
              </Link>
              <a
                href={waLink(`Hello — I would like a quote for ${r.from} to ${r.to}.`)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="route-bottom-whatsapp-cta"
                className="inline-flex items-center gap-2 border border-white/35 px-8 py-4 text-[12px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
              >
                <MessageCircle size={15} /> WhatsApp
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
